<template>
  <div class="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 flex items-center justify-center p-4">
    <div class="max-w-md w-full">
      <!-- Loading State -->
      <div v-if="isValidating" class="text-center fade-in">
        <div class="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6 backdrop-blur-sm">
          <div class="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
        </div>
        <h1 class="text-2xl font-bold text-white mb-2">Verifying Your Access</h1>
        <p class="text-primary-200">Please wait while we validate your link...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center fade-in">
        <div class="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <font-awesome-icon icon="exclamation-triangle" class="text-red-300 text-3xl" />
        </div>
        <h1 class="text-2xl font-bold text-white mb-2">Unable to Verify</h1>
        <p class="text-primary-200 mb-6">{{ error }}</p>
        <button
          @click="retry"
          class="btn bg-white text-primary-700 hover:bg-primary-50"
        >
          Try Again
        </button>
      </div>

      <!-- Success State (brief before redirect) -->
      <div v-else-if="isSuccess" class="text-center fade-in">
        <div class="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <font-awesome-icon icon="check" class="text-green-300 text-3xl" />
        </div>
        <h1 class="text-2xl font-bold text-white mb-2">Access Verified!</h1>
        <p class="text-primary-200">Redirecting to closing dashboard...</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import apiService from '@/api/apiService'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Landing',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()

    const isValidating = ref(true)
    const isSuccess = ref(false)
    const error = ref(null)

    const validateToken = async () => {
      const token = route.params.token

      if (!token) {
        console.log('[Landing] No token provided')
        error.value = 'No access token provided.'
        isValidating.value = false
        return
      }

      console.log('[Landing] Validating token:', token)

      // Clear ALL localStorage items when validating a new token
      // This ensures a completely clean session state for the new token
      const keysToRemove = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key))
      
      // Also clear the Vuex store session state
      store.dispatch('clearSession')

      try {
        const data = await apiService.validateToken(token)

        console.log('[Landing] Token validated successfully:', data)

        // Store session data
        store.dispatch('setSession', {
          sessionToken: data.session_token,
          settlementAgent: data.settlement_agent,
          loan: data.loan,
          closingRequest: data.closing_request,
        })

        // Also store loan officer if present
        if (data.loan_officer) {
          store.dispatch('setLoanOfficer', data.loan_officer)
        }

        isValidating.value = false
        isSuccess.value = true

        // Redirect to dashboard after brief success message
        setTimeout(() => {
          router.push({ name: 'Dashboard' })
        }, 1000)

      } catch (err) {
        console.error('[Landing] Token validation failed:', err)

        isValidating.value = false

        if (err.response?.status === 404) {
          error.value = 'This link is invalid or has expired. Please contact the loan officer for a new link.'
        } else if (err.response?.status === 400) {
          error.value = 'Invalid link format. Please use the link from your email.'
        } else {
          error.value = 'Unable to verify your access. Please try again later.'
        }
      }
    }

    const retry = () => {
      isValidating.value = true
      error.value = null
      validateToken()
    }

    onMounted(() => {
      validateToken()
    })

    return {
      isValidating,
      isSuccess,
      error,
      retry,
    }
  },
}
</script>





